import { useState, useMemo } from "react";
import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Pencil, Trash2, ClipboardList, Ruler, Calendar } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";
import { isClinicalStructuredRecord } from "@/lib/clinical-records";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronUp, ArrowUp, ArrowDown } from "lucide-react";

interface AnamnesisRecord {
  type: "anamnesis" | "clinical_record" | "clinical_note" | "exam" | "report";
  structure_type?: "anamnesis" | "antropometry";
  date: string;
  event_date?: string;
  is_main_record: boolean;
  name?: string | null;
  title?: string | null;
  clinical_note: string | null;
  description?: string | null;
  notes?: string | null;
  findings?: string | null;
  conclusion?: string | null;
  clinical_history?: string | null;
  category?: string | null;
  note_type: string | null;
  report_type?: string | null;
  exam_type?: string | null;
  // Campos para antropometria
  measurement_type?: string;
  measurement_type_slug?: string;
  value?: number;
  unit?: string;
}

interface SmartfileSession {
  id: string;
  file_name: string;
  content_type: string | null;
  ai_summary?: string | null;
  created_at: string;
  structured_records?: any[] | null;
  patient_id: string | null;
  status: string | null;
}

interface SmartfileAnamnesisDetailsProps {
  session: SmartfileSession;
  onRefresh: () => void;
  onRecordsChange?: (updatedRecords: any[]) => void;
  expandAllByDefault?: boolean;
}

interface EditableAnamnesisRecord extends AnamnesisRecord {
  _index?: number;
}

export function SmartfileAnamnesisDetails({ session, onRefresh, onRecordsChange, expandAllByDefault = false }: SmartfileAnamnesisDetailsProps) {
  const isMobile = useIsMobile();

  const getClinicalEventDate = (record: AnamnesisRecord): string => {
    return record.date || record.event_date || session.created_at;
  };

  const getClinicalText = (record: AnamnesisRecord): string => {
    const candidates = [
      record.clinical_note,
      record.description,
      record.notes,
      record.findings,
      record.conclusion,
      record.clinical_history,
    ];
    const first = candidates.find((value) => typeof value === "string" && value.trim().length > 0);
    return first || "Sem conteúdo clínico disponível.";
  };

  const getClinicalKind = (record: AnamnesisRecord): string => {
    if (record.note_type?.trim()) return record.note_type;
    if (record.category?.trim()) return record.category;
    if (record.report_type?.trim()) return record.report_type;
    if (record.exam_type?.trim()) return record.exam_type;
    return "Registro Clínico";
  };

  // Buscar o registro principal de anamnese (is_main_record: true, structure_type: "anamnesis")
  const mainRecord = useMemo(() => {
    return (session.structured_records || []).find((record: any) =>
      typeof record === 'object' &&
      record !== null &&
      'type' in record &&
      isClinicalStructuredRecord(record) &&
      ((record as any).is_main_record === true || (record as any).is_main_record === 'true') &&
      (!(record as any).structure_type || (record as any).structure_type === 'anamnesis')
    ) as AnamnesisRecord | undefined;
  }, [session.structured_records]);

  // Buscar o registro principal de antropometria (is_main_record: true, structure_type: "antropometry")
  const mainAnthropometryRecord = useMemo(() => {
    return (session.structured_records || []).find((record: any) =>
      typeof record === 'object' &&
      record !== null &&
      'type' in record &&
      record.type === 'anamnesis' &&
      ((record as any).is_main_record === true || (record as any).is_main_record === 'true') &&
      (record as any).structure_type === 'antropometry'
    ) as AnamnesisRecord | undefined;
  }, [session.structured_records]);

  // Separar registros por structure_type (excluir registros principais)
  const allRecords = useMemo(() => {
    return (session.structured_records || [])
      .filter((record: any): record is AnamnesisRecord =>
        typeof record === 'object' &&
        record !== null &&
        'type' in record &&
        isClinicalStructuredRecord(record) &&
        record.structure_type !== 'antropometry' &&
        !(record as any).is_main_record
      );
  }, [session.structured_records]);

  // Filtrar anamnese (structure_type: "anamnesis" ou sem structure_type)
  const anamnesisRecords: EditableAnamnesisRecord[] = useMemo(() => {
    return allRecords
      .filter((record: AnamnesisRecord) =>
        !record.structure_type || record.structure_type === 'anamnesis'
      )
      .map((record: AnamnesisRecord, index: number) => ({
        ...record,
        _index: index
      }));
  }, [allRecords]);

  // Filtrar antropometria (structure_type: "antropometry")
  const anthropometryRecords: EditableAnamnesisRecord[] = useMemo(() => {
    return allRecords
      .filter((record: AnamnesisRecord) =>
        record.structure_type === 'antropometry'
      )
      .map((record: AnamnesisRecord, index: number) => ({
        ...record,
        _index: index
      }));
  }, [allRecords]);

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingMainRecord, setEditingMainRecord] = useState(false);
  const [editedRecords, setEditedRecords] = useState<EditableAnamnesisRecord[]>(anamnesisRecords);
  const [editedMainRecord, setEditedMainRecord] = useState<AnamnesisRecord | undefined>(mainRecord);
  const [isSaving, setIsSaving] = useState(false);
  const [dateSortOrder, setDateSortOrder] = useState<'asc' | 'desc'>('desc');
  const [expandedDateGroups, setExpandedDateGroups] = useState<Map<string, boolean>>(new Map());

  // Atualizar editedRecords quando anamnesisRecords mudar
  React.useEffect(() => {
    setEditedRecords(anamnesisRecords);
    setEditedMainRecord(mainRecord);
  }, [session.structured_records]);

  const handleSave = async (index: number) => {
    setIsSaving(true);
    try {
      const updatedRecords = [...(session.structured_records || [])];

      let anamnesisCount = 0;
      let realIndex = -1;

      for (let i = 0; i < updatedRecords.length; i++) {
        const record = updatedRecords[i];
        if (typeof record === 'object' && record !== null && 'type' in record &&
          isClinicalStructuredRecord(record) &&
          !(record as any).is_main_record &&
          (!(record as any).structure_type || (record as any).structure_type === 'anamnesis')) {
          if (anamnesisCount === index) {
            realIndex = i;
            break;
          }
          anamnesisCount++;
        }
      }

      if (realIndex !== -1) {
        updatedRecords[realIndex] = editedRecords[index];

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Registro atualizado com sucesso!");
        setEditingIndex(null);
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      toast.error("❌ Erro ao salvar alterações");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (index: number) => {
    try {
      const updatedRecords = [...(session.structured_records || [])];

      let anamnesisCount = 0;
      let realIndex = -1;

      for (let i = 0; i < updatedRecords.length; i++) {
        const record = updatedRecords[i];
        if (typeof record === 'object' && record !== null && 'type' in record &&
          isClinicalStructuredRecord(record) &&
          !(record as any).is_main_record &&
          (!(record as any).structure_type || (record as any).structure_type === 'anamnesis')) {
          if (anamnesisCount === index) {
            realIndex = i;
            break;
          }
          anamnesisCount++;
        }
      }

      if (realIndex !== -1) {
        updatedRecords.splice(realIndex, 1);

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Registro excluído com sucesso!");
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao excluir:', error);
      toast.error("❌ Erro ao excluir registro");
    }
  };

  const handleCancel = () => {
    setEditedRecords(anamnesisRecords);
    setEditingIndex(null);
  };

  const handleSaveMainRecord = async () => {
    setIsSaving(true);
    try {
      const updatedRecords = [...(session.structured_records || [])];

      const mainRecordIndex = updatedRecords.findIndex((record: any) =>
        typeof record === 'object' &&
        record !== null &&
        'type' in record &&
        isClinicalStructuredRecord(record) &&
        ((record as any).is_main_record === true || (record as any).is_main_record === 'true')
      );

      if (mainRecordIndex !== -1 && editedMainRecord) {
        updatedRecords[mainRecordIndex] = editedMainRecord;

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Nome do documento atualizado com sucesso!");
        setEditingMainRecord(false);
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      toast.error("❌ Erro ao salvar alterações");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelMainRecord = () => {
    setEditedMainRecord(mainRecord);
    setEditingMainRecord(false);
  };

  // Estados para antropometria
  const [editingAnthropometryIndex, setEditingAnthropometryIndex] = useState<number | null>(null);
  const [editingMainAnthropometryRecord, setEditingMainAnthropometryRecord] = useState(false);
  const [editedAnthropometryRecords, setEditedAnthropometryRecords] = useState<EditableAnamnesisRecord[]>(anthropometryRecords);
  const [editedMainAnthropometryRecord, setEditedMainAnthropometryRecord] = useState<AnamnesisRecord | undefined>(mainAnthropometryRecord);
  const [isSavingAnthropometry, setIsSavingAnthropometry] = useState(false);
  const [dateSortOrderAnthropometry, setDateSortOrderAnthropometry] = useState<'asc' | 'desc'>('desc');
  const [expandedDateGroupsAnthropometry, setExpandedDateGroupsAnthropometry] = useState<Map<string, boolean>>(new Map());

  React.useEffect(() => {
    setEditedAnthropometryRecords(anthropometryRecords);
    setEditedMainAnthropometryRecord(mainAnthropometryRecord);
  }, [session.structured_records]);

  const handleSaveAnthropometry = async (index: number) => {
    setIsSavingAnthropometry(true);
    try {
      const updatedRecords = [...(session.structured_records || [])];

      let anthropometryCount = 0;
      let realIndex = -1;

      for (let i = 0; i < updatedRecords.length; i++) {
        const record = updatedRecords[i];
        if (typeof record === 'object' && record !== null && 'type' in record &&
          record.type === 'anamnesis' &&
          !(record as any).is_main_record &&
          (record as any).structure_type === 'antropometry') {
          if (anthropometryCount === index) {
            realIndex = i;
            break;
          }
          anthropometryCount++;
        }
      }

      if (realIndex !== -1) {
        updatedRecords[realIndex] = editedAnthropometryRecords[index];

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Registro de antropometria atualizado com sucesso!");
        setEditingAnthropometryIndex(null);
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      toast.error("❌ Erro ao salvar alterações");
    } finally {
      setIsSavingAnthropometry(false);
    }
  };

  const handleDeleteAnthropometry = async (index: number) => {
    try {
      const updatedRecords = [...(session.structured_records || [])];

      let anthropometryCount = 0;
      let realIndex = -1;

      for (let i = 0; i < updatedRecords.length; i++) {
        const record = updatedRecords[i];
        if (typeof record === 'object' && record !== null && 'type' in record &&
          record.type === 'anamnesis' &&
          !(record as any).is_main_record &&
          (record as any).structure_type === 'antropometry') {
          if (anthropometryCount === index) {
            realIndex = i;
            break;
          }
          anthropometryCount++;
        }
      }

      if (realIndex !== -1) {
        updatedRecords.splice(realIndex, 1);

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Registro de antropometria excluído com sucesso!");
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao excluir:', error);
      toast.error("❌ Erro ao excluir registro");
    }
  };

  const handleCancelAnthropometry = () => {
    setEditedAnthropometryRecords(anthropometryRecords);
    setEditingAnthropometryIndex(null);
  };

  const handleSaveMainAnthropometryRecord = async () => {
    setIsSavingAnthropometry(true);
    try {
      const updatedRecords = [...(session.structured_records || [])];

      const mainAnthropometryRecordIndex = updatedRecords.findIndex((record: any) =>
        typeof record === 'object' &&
        record !== null &&
        'type' in record &&
        record.type === 'anamnesis' &&
        ((record as any).is_main_record === true || (record as any).is_main_record === 'true') &&
        (record as any).structure_type === 'antropometry'
      );

      if (mainAnthropometryRecordIndex !== -1 && editedMainAnthropometryRecord) {
        updatedRecords[mainAnthropometryRecordIndex] = editedMainAnthropometryRecord;

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Nome do documento de antropometria atualizado com sucesso!");
        setEditingMainAnthropometryRecord(false);
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      toast.error("❌ Erro ao salvar alterações");
    } finally {
      setIsSavingAnthropometry(false);
    }
  };

  const handleCancelMainAnthropometryRecord = () => {
    setEditedMainAnthropometryRecord(mainAnthropometryRecord);
    setEditingMainAnthropometryRecord(false);
  };

  if (anamnesisRecords.length === 0 && anthropometryRecords.length === 0 && !mainRecord) {
    return (
      <div className="text-center py-8 text-muted-foreground bg-background border-t">
        <p className="text-sm">Nenhuma anamnese ou antropometria encontrada nesta sessão</p>
      </div>
    );
  }

  // Agrupar anamnese por mês/ano
  const groupedByDate = anamnesisRecords.reduce((acc, record, index) => {
    const date = getClinicalEventDate(record);
    const dateObj = new Date(date);
    const monthYearKey = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}`;
    const displayKey = `${String(dateObj.getMonth() + 1).padStart(2, '0')}/${dateObj.getFullYear()}`;

    if (!acc[monthYearKey]) {
      acc[monthYearKey] = {
        displayKey,
        records: []
      };
    }
    acc[monthYearKey].records.push({ ...record, _index: index });
    return acc;
  }, {} as Record<string, { displayKey: string; records: EditableAnamnesisRecord[] }>);

  // Agrupar antropometria por mês/ano
  const groupedByDateAnthropometry = anthropometryRecords.reduce((acc, record, index) => {
    const date = record.date || session.created_at;
    const dateObj = new Date(date);
    const monthYearKey = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}`;
    const displayKey = `${String(dateObj.getMonth() + 1).padStart(2, '0')}/${dateObj.getFullYear()}`;

    if (!acc[monthYearKey]) {
      acc[monthYearKey] = {
        displayKey,
        records: []
      };
    }
    acc[monthYearKey].records.push({ ...record, _index: index });
    return acc;
  }, {} as Record<string, { displayKey: string; records: EditableAnamnesisRecord[] }>);

  const sortedDates = Object.keys(groupedByDate).sort((a, b) => {
    const dateA = new Date(a + '-01').getTime();
    const dateB = new Date(b + '-01').getTime();
    return dateSortOrder === 'asc' ? dateA - dateB : dateB - dateA;
  });

  const sortedDatesAnthropometry = Object.keys(groupedByDateAnthropometry).sort((a, b) => {
    const dateA = new Date(a + '-01').getTime();
    const dateB = new Date(b + '-01').getTime();
    return dateSortOrderAnthropometry === 'asc' ? dateA - dateB : dateB - dateA;
  });

  const toggleDateGroup = (dateKey: string, isOpen: boolean) => {
    setExpandedDateGroups((prev) => {
      const next = new Map(prev);
      next.set(dateKey, isOpen);
      return next;
    });
  };

  const toggleDateSort = () => {
    setDateSortOrder((prev) => prev === 'asc' ? 'desc' : 'asc');
  };

  const toggleDateSortAnthropometry = () => {
    setDateSortOrderAnthropometry((prev) => prev === 'asc' ? 'desc' : 'asc');
  };

  const toggleDateGroupAnthropometry = (dateKey: string, isOpen: boolean) => {
    setExpandedDateGroupsAnthropometry((prev) => {
      const next = new Map(prev);
      next.set(dateKey, isOpen);
      return next;
    });
  };

  const noteTypeOptions = [
    "Queixa principal",
    "Sintoma atual",
    "Diagnóstico prévio",
    "Exame físico",
    "Histórico familiar",
    "Histórico pessoal",
    "Alergias",
    "Medicações em uso"
  ];

  // Função para renderizar uma linha de registro de anamnese
  const renderRecordRow = (record: EditableAnamnesisRecord, groupIndex: number, dateKey: string) => {
    const originalIndex = record._index ?? groupIndex;
    const isEditing = editingIndex === originalIndex;

    return (
      <React.Fragment key={`${dateKey}-${originalIndex}`}>
        {isEditing ? (
          <tr className="border-b border-border">
            <td colSpan={4} className="p-3 bg-muted/30">
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Data</Label>
                    <Input
                      type="date"
                      value={editedRecords[originalIndex]?.date || editedRecords[originalIndex]?.event_date || ''}
                      onChange={(e) => {
                        const updated = [...editedRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          date: e.target.value,
                          event_date: e.target.value
                        };
                        setEditedRecords(updated);
                      }}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Tipo</Label>
                    <select
                      value={editedRecords[originalIndex]?.note_type || ''}
                      onChange={(e) => {
                        const updated = [...editedRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          note_type: e.target.value || null
                        };
                        setEditedRecords(updated);
                      }}
                      className="flex h-8 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="">Selecione...</option>
                      {noteTypeOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Nota clínica</Label>
                  <Textarea
                    value={editedRecords[originalIndex]?.clinical_note || editedRecords[originalIndex]?.description || ''}
                    onChange={(e) => {
                      const updated = [...editedRecords];
                      updated[originalIndex] = {
                        ...updated[originalIndex],
                        clinical_note: e.target.value || null,
                        description: e.target.value || null
                      };
                      setEditedRecords(updated);
                    }}
                    className="min-h-[60px]"
                    placeholder="Nota clínica..."
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <Button size="sm" onClick={() => handleSave(originalIndex)} disabled={isSaving}>
                    {isSaving ? "Salvando..." : "Salvar"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleCancel} disabled={isSaving}>
                    Cancelar
                  </Button>
                </div>
              </div>
            </td>
          </tr>
        ) : (
          <tr className="border-b border-border hover:bg-muted/30 transition-colors">
            <td className="p-3 text-sm text-muted-foreground w-[120px]">
              <div className="truncate">{format(new Date(getClinicalEventDate(record)), "dd/MM/yyyy")}</div>
            </td>
            <td className="p-3 w-[300px]">
              <div className="text-sm text-muted-foreground" title={getClinicalText(record)}>
                {getClinicalText(record)}
              </div>
            </td>
            <td className="p-3 w-[200px]">
              <Badge variant="outline" className="text-xs">
                {getClinicalKind(record)}
              </Badge>
            </td>
            <td className="p-3 text-right w-[120px]">
              <div className="flex gap-1 justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingIndex(originalIndex)}
                  className="h-7 px-2"
                >
                  <Pencil className="h-3 w-3" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                      <AlertDialogDescription>
                        Tem certeza que deseja excluir esta nota clínica?
                        <br />
                        <span className="font-medium mt-2 block">
                          {format(new Date(getClinicalEventDate(record)), "dd/MM/yyyy")} - {getClinicalKind(record)}
                        </span>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleDelete(originalIndex)}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Excluir
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  // Função para renderizar linha de antropometria
  const renderAnthropometryRow = (record: EditableAnamnesisRecord, groupIndex: number, dateKey: string) => {
    const originalIndex = record._index ?? groupIndex;
    const isEditing = editingAnthropometryIndex === originalIndex;

    const measurementTypeLabels: Record<string, string> = {
      peso: 'Peso',
      altura: 'Altura',
      circunferencia_cintura: 'Circunferência da Cintura',
      circunferencia_quadril: 'Circunferência do Quadril',
      imc: 'IMC',
      gordura_corporal: 'Gordura Corporal',
      massa_magra: 'Massa Magra',
      rcq: 'RCQ'
    };

    return (
      <React.Fragment key={`${dateKey}-anthro-${originalIndex}`}>
        {isEditing ? (
          <tr className="border-b border-border">
            <td colSpan={5} className="p-3 bg-muted/30">
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs">Data</Label>
                    <Input
                      type="date"
                      value={editedAnthropometryRecords[originalIndex]?.date || ''}
                      onChange={(e) => {
                        const updated = [...editedAnthropometryRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          date: e.target.value
                        };
                        setEditedAnthropometryRecords(updated);
                      }}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Dado</Label>
                    <Input
                      value={editedAnthropometryRecords[originalIndex]?.measurement_type || ''}
                      onChange={(e) => {
                        const updated = [...editedAnthropometryRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          measurement_type: e.target.value || undefined
                        };
                        setEditedAnthropometryRecords(updated);
                      }}
                      placeholder="Ex: Peso, Altura"
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Medida</Label>
                    <Input
                      type="number"
                      step="any"
                      value={editedAnthropometryRecords[originalIndex]?.value ?? ''}
                      onChange={(e) => {
                        const updated = [...editedAnthropometryRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          value: e.target.value ? parseFloat(e.target.value) : undefined
                        };
                        setEditedAnthropometryRecords(updated);
                      }}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Unidade</Label>
                    <Input
                      value={editedAnthropometryRecords[originalIndex]?.unit || ''}
                      onChange={(e) => {
                        const updated = [...editedAnthropometryRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          unit: e.target.value || undefined
                        };
                        setEditedAnthropometryRecords(updated);
                      }}
                      placeholder="kg, cm, m, %"
                      className="h-8"
                    />
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button size="sm" onClick={() => handleSaveAnthropometry(originalIndex)} disabled={isSavingAnthropometry}>
                    {isSavingAnthropometry ? "Salvando..." : "Salvar"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleCancelAnthropometry} disabled={isSavingAnthropometry}>
                    Cancelar
                  </Button>
                </div>
              </div>
            </td>
          </tr>
        ) : (
          <tr className="border-b border-border hover:bg-muted/30 transition-colors">
            <td className="p-3 text-sm text-muted-foreground w-[120px]">
              <div className="truncate">{format(new Date(record.date), "dd/MM/yyyy")}</div>
            </td>
            <td className="p-3 w-[200px]">
              <div className="text-sm font-medium truncate">
                {measurementTypeLabels[record.measurement_type_slug || ''] || record.measurement_type || '–'}
              </div>
            </td>
            <td className="p-3 w-[120px]">
              <div className="text-sm truncate">
                {record.value !== undefined && record.value !== null ? record.value : '–'}
              </div>
            </td>
            <td className="p-3 w-[100px]">
              <div className="text-sm truncate">
                {record.unit || '–'}
              </div>
            </td>
            <td className="p-3 text-right w-[120px]">
              <div className="flex gap-1 justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingAnthropometryIndex(originalIndex)}
                  className="h-7 px-2"
                >
                  <Pencil className="h-3 w-3" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                      <AlertDialogDescription>
                        Tem certeza que deseja excluir esta medida antropométrica?
                        <br />
                        <span className="font-medium mt-2 block">
                          {format(new Date(record.date), "dd/MM/yyyy")} - {measurementTypeLabels[record.measurement_type_slug || ''] || record.measurement_type || 'Medida'}
                        </span>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleDeleteAnthropometry(originalIndex)}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Excluir
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  // Função para renderizar card mobile de anamnese
  const renderMobileAnamnesisCard = (record: EditableAnamnesisRecord, groupIndex: number, dateKey: string) => {
    const originalIndex = record._index ?? groupIndex;
    const isEditing = editingIndex === originalIndex;

    return (
      <Card key={`${dateKey}-${originalIndex}-mobile`} className="mb-3">
        <CardContent className="p-4">
          {isEditing ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Data</Label>
                  <Input
                    type="date"
                    value={editedRecords[originalIndex]?.date || editedRecords[originalIndex]?.event_date || ''}
                    onChange={(e) => {
                      const updated = [...editedRecords];
                      updated[originalIndex] = { ...updated[originalIndex], date: e.target.value, event_date: e.target.value };
                      setEditedRecords(updated);
                    }}
                    className="h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs">Tipo</Label>
                  <select
                    value={editedRecords[originalIndex]?.note_type || ''}
                    onChange={(e) => {
                      const updated = [...editedRecords];
                      updated[originalIndex] = { ...updated[originalIndex], note_type: e.target.value || null };
                      setEditedRecords(updated);
                    }}
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                  >
                    <option value="">Selecione...</option>
                    {noteTypeOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Nota clínica</Label>
                <Textarea
                  value={editedRecords[originalIndex]?.clinical_note || editedRecords[originalIndex]?.description || ''}
                  onChange={(e) => {
                    const updated = [...editedRecords];
                    updated[originalIndex] = {
                      ...updated[originalIndex],
                      clinical_note: e.target.value || null,
                      description: e.target.value || null,
                    };
                    setEditedRecords(updated);
                  }}
                  className="min-h-[80px]"
                  placeholder="Nota clínica..."
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button size="sm" onClick={() => handleSave(originalIndex)} disabled={isSaving} className="flex-1">
                  {isSaving ? "Salvando..." : "Salvar"}
                </Button>
                <Button size="sm" variant="outline" onClick={handleCancel} disabled={isSaving} className="flex-1">
                  Cancelar
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Cabeçalho: Título, Data e Botões de Ação */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <ClipboardList className="h-4 w-4 text-muted-foreground" />
                    <Badge variant="outline" className="text-xs">
                      {getClinicalKind(record)}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    {format(new Date(getClinicalEventDate(record)), "dd/MM/yyyy")}
                  </div>
                </div>
                <div className="flex gap-1 ml-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingIndex(originalIndex)}
                    className="h-8 px-2 text-xs"
                  >
                    <Pencil className="h-3 w-3 mr-1" />
                    Editar
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Excluir
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                        <AlertDialogDescription>
                          Tem certeza que deseja excluir esta nota clínica?
                          <br />
                          <span className="font-medium mt-2 block">
                            {format(new Date(getClinicalEventDate(record)), "dd/MM/yyyy")} - {getClinicalKind(record)}
                          </span>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDelete(originalIndex)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                          Excluir
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>

              {/* Descrição/Nota Clínica */}
              {getClinicalText(record) && (
                <p className="text-sm text-foreground leading-relaxed">
                  {getClinicalText(record)}
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  // Função para renderizar card mobile de antropometria
  const renderMobileAnthropometryCard = (record: EditableAnamnesisRecord, groupIndex: number, dateKey: string) => {
    const originalIndex = record._index ?? groupIndex;
    const isEditing = editingAnthropometryIndex === originalIndex;

    const measurementTypeLabels: Record<string, string> = {
      peso: 'Peso',
      altura: 'Altura',
      circunferencia_cintura: 'Circunferência da Cintura',
      circunferencia_quadril: 'Circunferência do Quadril',
      imc: 'IMC',
      gordura_corporal: 'Gordura Corporal',
      massa_magra: 'Massa Magra',
      rcq: 'RCQ'
    };

    return (
      <Card key={`${dateKey}-anthro-${originalIndex}-mobile`} className="mb-3">
        <CardContent className="p-4">
          {isEditing ? (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Data</Label>
                <Input
                  type="date"
                  value={editedAnthropometryRecords[originalIndex]?.date || ''}
                  onChange={(e) => {
                    const updated = [...editedAnthropometryRecords];
                    updated[originalIndex] = { ...updated[originalIndex], date: e.target.value };
                    setEditedAnthropometryRecords(updated);
                  }}
                  className="h-9"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Dado</Label>
                  <Input
                    value={editedAnthropometryRecords[originalIndex]?.measurement_type || ''}
                    onChange={(e) => {
                      const updated = [...editedAnthropometryRecords];
                      updated[originalIndex] = { ...updated[originalIndex], measurement_type: e.target.value || undefined };
                      setEditedAnthropometryRecords(updated);
                    }}
                    placeholder="Ex: Peso, Altura"
                    className="h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs">Medida</Label>
                  <Input
                    type="number"
                    step="any"
                    value={editedAnthropometryRecords[originalIndex]?.value ?? ''}
                    onChange={(e) => {
                      const updated = [...editedAnthropometryRecords];
                      updated[originalIndex] = { ...updated[originalIndex], value: e.target.value ? parseFloat(e.target.value) : undefined };
                      setEditedAnthropometryRecords(updated);
                    }}
                    className="h-9"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Unidade</Label>
                <Input
                  value={editedAnthropometryRecords[originalIndex]?.unit || ''}
                  onChange={(e) => {
                    const updated = [...editedAnthropometryRecords];
                    updated[originalIndex] = { ...updated[originalIndex], unit: e.target.value || undefined };
                    setEditedAnthropometryRecords(updated);
                  }}
                  placeholder="kg, cm, m, %"
                  className="h-9"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button size="sm" onClick={() => handleSaveAnthropometry(originalIndex)} disabled={isSavingAnthropometry} className="flex-1">
                  {isSavingAnthropometry ? "Salvando..." : "Salvar"}
                </Button>
                <Button size="sm" variant="outline" onClick={handleCancelAnthropometry} disabled={isSavingAnthropometry} className="flex-1">
                  Cancelar
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Cabeçalho: Título, Data e Botões de Ação */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h4 className="font-semibold text-sm leading-tight mb-1">
                    {measurementTypeLabels[record.measurement_type_slug || ''] || record.measurement_type || 'Medida'}
                  </h4>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    {format(new Date(record.date), "dd/MM/yyyy")}
                  </div>
                </div>
                <div className="flex gap-1 ml-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingAnthropometryIndex(originalIndex)}
                    className="h-8 px-2 text-xs"
                  >
                    <Pencil className="h-3 w-3 mr-1" />
                    Editar
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Excluir
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                        <AlertDialogDescription>
                          Tem certeza que deseja excluir esta medida antropométrica?
                          <br />
                          <span className="font-medium mt-2 block">
                            {format(new Date(record.date), "dd/MM/yyyy")} - {measurementTypeLabels[record.measurement_type_slug || ''] || record.measurement_type || 'Medida'}
                          </span>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDeleteAnthropometry(originalIndex)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                          Excluir
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>

              {/* Duas colunas: Valor, Unidade */}
              <div className="grid grid-cols-2 gap-4 pt-3 border-t border-border">
                <div>
                  <span className="text-xs text-muted-foreground block mb-1">Valor</span>
                  <span className="text-sm font-medium">{record.value ?? '–'}</span>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground block mb-1">Unidade</span>
                  <span className="text-sm font-medium">{record.unit || '–'}</span>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="bg-background border-t">
      {/* Seção: Título Editável (sem cabeçalho "Informações do Documento") */}
      {mainRecord && (
        <div className="mb-4 border-b border-border pb-4 px-4 pt-4">
          {editingMainRecord ? (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Nome do Documento</Label>
                <Input
                  value={editedMainRecord?.name || ''}
                  onChange={(e) => {
                    setEditedMainRecord({
                      ...editedMainRecord!,
                      name: e.target.value || null
                    });
                  }}
                  placeholder="Ex: Anamnese - 28/11/2025"
                  className="h-8"
                />
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSaveMainRecord} disabled={isSaving}>
                  {isSaving ? "Salvando..." : "Salvar"}
                </Button>
                <Button size="sm" variant="outline" onClick={handleCancelMainRecord} disabled={isSaving}>
                  Cancelar
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-xs text-muted-foreground">Nome do Documento</Label>
                <p className="text-sm font-medium mt-1">
                  {mainRecord.name || 'Sem nome'}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditingMainRecord(true)}
                className="h-7 px-2"
              >
                <Pencil className="h-3 w-3 mr-1" />
                Editar
              </Button>
            </div>
          )}
        </div>
      )}

      {mainRecord && anamnesisRecords.length === 0 && anthropometryRecords.length === 0 && (
        <div className="px-4 pb-4">
          <Card>
            <CardContent className="p-4 space-y-2">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Registro Clínico</p>
              <p className="text-sm leading-relaxed whitespace-pre-wrap">
                {getClinicalText(mainRecord)}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Seção: Anamnese */}
      {anamnesisRecords.length > 0 && (
        <div className="mb-6">
          {/* Mobile: Cards View */}
          {isMobile ? (
            <div className="p-3">
              {sortedDates.map((monthYearKey) => {
                const dateGroup = groupedByDate[monthYearKey];
                const dateRecords = dateGroup.records;
                const displayKey = dateGroup.displayKey;
                const defaultExpanded = expandAllByDefault;
                const manualState = expandedDateGroups.get(monthYearKey);
                const isExpanded = manualState !== undefined ? manualState : defaultExpanded;

                return (
                  <div key={monthYearKey} className="mb-4">
                    <Collapsible open={isExpanded} onOpenChange={(open) => toggleDateGroup(monthYearKey, open)}>
                      <CollapsibleTrigger asChild>
                        <div className="flex items-center gap-2 text-xs font-semibold text-primary cursor-pointer bg-primary/5 hover:bg-primary/10 rounded-lg px-3 py-2 mb-2 transition-colors">
                          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          <ClipboardList className="h-4 w-4" />
                          <span>📅 {displayKey}</span>
                          <span className="ml-auto font-normal text-muted-foreground mr-2">{isExpanded ? '- Ver menos' : '+ Ver mais'}</span>
                          <Badge variant="secondary">
                            {dateRecords.length} registro{dateRecords.length !== 1 ? 's' : ''}
                          </Badge>
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        {dateRecords.map((record, groupIndex) => renderMobileAnamnesisCard(record, groupIndex, monthYearKey))}
                      </CollapsibleContent>
                    </Collapsible>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Desktop: Table View */
            <div className="overflow-x-auto">
              <table className="w-full table-fixed">
                <colgroup>
                  <col style={{ width: '120px' }} />
                  <col style={{ width: '300px' }} />
                  <col style={{ width: '200px' }} />
                  <col style={{ width: '120px' }} />
                </colgroup>
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Data
                    </th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Nota clínica
                    </th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Tipo
                    </th>
                    <th className="p-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedDates.map((monthYearKey) => {
                    const dateGroup = groupedByDate[monthYearKey];
                    const dateRecords = dateGroup.records;
                    const displayKey = dateGroup.displayKey;
                    const defaultExpanded = expandAllByDefault;
                    const manualState = expandedDateGroups.get(monthYearKey);
                    const isExpanded = manualState !== undefined ? manualState : defaultExpanded;

                    return (
                      <React.Fragment key={monthYearKey}>
                        <tr className="bg-primary/5 border-y-2 border-primary/20">
                          <td colSpan={4} className="p-2">
                            <Collapsible open={isExpanded} onOpenChange={(open) => toggleDateGroup(monthYearKey, open)}>
                              <CollapsibleTrigger asChild>
                                <div className="flex items-center gap-2 text-xs font-semibold text-primary cursor-pointer hover:bg-primary/10 rounded px-2 py-1 transition-colors w-full">
                                  {isExpanded ? (
                                    <ChevronUp className="h-4 w-4" />
                                  ) : (
                                    <ChevronDown className="h-4 w-4" />
                                  )}
                                  <ClipboardList className="h-4 w-4" />
                                  <span>📅 {displayKey}</span>
                                  <span className="ml-auto font-normal text-muted-foreground mr-2">
                                    {isExpanded ? '- Ver menos' : '+ Ver mais'}
                                  </span>
                                  <Badge variant="secondary">
                                    {dateRecords.length} registro{dateRecords.length !== 1 ? 's' : ''}
                                  </Badge>
                                </div>
                              </CollapsibleTrigger>
                            </Collapsible>
                          </td>
                        </tr>

                        {isExpanded && (
                          <>
                            {dateRecords.map((record, groupIndex) => {
                              return renderRecordRow(record, groupIndex, monthYearKey);
                            })}
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="p-3 text-xs text-muted-foreground border-t border-border bg-muted/30">
            Total: {anamnesisRecords.length} registro(s) de anamnese em {sortedDates.length} mês(es)/ano(s)
          </div>
        </div>
      )}

      {/* Seção: Título Editável - Antropometria (sem cabeçalho "Informações do Documento") */}
      {mainAnthropometryRecord && (
        <div className="mb-4 border-b border-border pb-4 px-4 pt-4">
          {editingMainAnthropometryRecord && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Nome do Documento</Label>
                <Input
                  value={editedMainAnthropometryRecord?.name || ''}
                  onChange={(e) => {
                    setEditedMainAnthropometryRecord({
                      ...editedMainAnthropometryRecord!,
                      name: e.target.value || null
                    });
                  }}
                  placeholder="Ex: Antropometria - 28/11/2025"
                  className="h-8"
                />
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSaveMainAnthropometryRecord} disabled={isSavingAnthropometry}>
                  {isSavingAnthropometry ? "Salvando..." : "Salvar"}
                </Button>
                <Button size="sm" variant="outline" onClick={handleCancelMainAnthropometryRecord} disabled={isSavingAnthropometry}>
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Seção: Antropometria */}
      {anthropometryRecords.length > 0 && (
        <div>
          {/* Mobile: Cards View */}
          {isMobile ? (
            <div className="p-3">
              {sortedDatesAnthropometry.map((monthYearKey) => {
                const dateGroup = groupedByDateAnthropometry[monthYearKey];
                const dateRecords = dateGroup.records;
                const displayKey = dateGroup.displayKey;
                const defaultExpanded = expandAllByDefault;
                const manualState = expandedDateGroupsAnthropometry.get(monthYearKey);
                const isExpanded = manualState !== undefined ? manualState : defaultExpanded;

                return (
                  <div key={monthYearKey} className="mb-4">
                    <Collapsible open={isExpanded} onOpenChange={(open) => toggleDateGroupAnthropometry(monthYearKey, open)}>
                      <CollapsibleTrigger asChild>
                        <div className="flex items-center gap-2 text-xs font-semibold text-blue-600 cursor-pointer bg-blue-500/5 hover:bg-blue-500/10 rounded-lg px-3 py-2 mb-2 transition-colors">
                          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          <Ruler className="h-4 w-4" />
                          <span>📅 {displayKey}</span>
                          <span className="ml-auto font-normal text-muted-foreground mr-2">{isExpanded ? '- Ver menos' : '+ Ver mais'}</span>
                          <Badge variant="secondary">
                            {dateRecords.length} registro{dateRecords.length !== 1 ? 's' : ''}
                          </Badge>
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        {dateRecords.map((record, groupIndex) => renderMobileAnthropometryCard(record, groupIndex, monthYearKey))}
                      </CollapsibleContent>
                    </Collapsible>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Desktop: Table View */
            <div className="overflow-x-auto">
              <table className="w-full table-fixed">
                <colgroup>
                  <col style={{ width: '120px' }} />
                  <col style={{ width: '200px' }} />
                  <col style={{ width: '120px' }} />
                  <col style={{ width: '100px' }} />
                  <col style={{ width: '120px' }} />
                </colgroup>
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Data
                    </th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Dado
                    </th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Medida
                    </th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Unidade
                    </th>
                    <th className="p-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedDatesAnthropometry.map((monthYearKey) => {
                    const dateGroup = groupedByDateAnthropometry[monthYearKey];
                    const dateRecords = dateGroup.records;
                    const displayKey = dateGroup.displayKey;
                    const defaultExpanded = expandAllByDefault;
                    const manualState = expandedDateGroupsAnthropometry.get(monthYearKey);
                    const isExpanded = manualState !== undefined ? manualState : defaultExpanded;

                    return (
                      <React.Fragment key={monthYearKey}>
                        <tr className="bg-blue-500/5 border-y-2 border-blue-500/20">
                          <td colSpan={5} className="p-2">
                            <Collapsible open={isExpanded} onOpenChange={(open) => toggleDateGroupAnthropometry(monthYearKey, open)}>
                              <CollapsibleTrigger asChild>
                                <div className="flex items-center gap-2 text-xs font-semibold text-blue-600 cursor-pointer hover:bg-blue-500/10 rounded px-2 py-1 transition-colors w-full">
                                  {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                  <Ruler className="h-4 w-4" />
                                  <span>📅 {displayKey}</span>
                                  <span className="ml-auto font-normal text-muted-foreground mr-2">
                                    {isExpanded ? '- Ver menos' : '+ Ver mais'}
                                  </span>
                                  <Badge variant="secondary">
                                    {dateRecords.length} registro{dateRecords.length !== 1 ? 's' : ''}
                                  </Badge>
                                </div>
                              </CollapsibleTrigger>
                            </Collapsible>
                          </td>
                        </tr>

                        {isExpanded && (
                          <>
                            {dateRecords.map((record, groupIndex) => renderAnthropometryRow(record, groupIndex, monthYearKey))}
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="p-3 text-xs text-muted-foreground border-t border-border bg-muted/30">
            Total: {anthropometryRecords.length} registro(s) de antropometria em {sortedDatesAnthropometry.length} mês(es)/ano(s)
          </div>
        </div>
      )}
    </div>
  );
}

